import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";

import {
  AppLocale,
  CHOICE_STORAGE_KEY,
  DEFAULT_CHOICE,
  FALLBACK_LOCALE,
  LANGUAGE_CHOICES,
  LanguageChoice,
  LOCALE_STORAGE_KEY,
  LanguageVariants,
  SUPPORTED_LOCALES,
  choiceForLocale,
  defaultLocaleForChoice,
  localeBelongsToChoice,
  resolveLocaleFromVariants,
} from "src/app/config";

/**
 * Idioma de la INTERFAZ.
 *
 * El selector ofrece tres opciones (Español / English / Català). El español es
 * genérico: no se le pregunta al usuario si su colegio es de España o de
 * LatAm. Al iniciar sesión, `applyLanguageVariants()` resuelve el locale
 * concreto con el mapa que manda el backend en el perfil (ver
 * `UserService.getUserProfileData`).
 *
 * El idioma de CONTENIDO (el `lang=` que se le manda al backend) vive aparte,
 * en `ContentLanguageService`.
 */
@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  constructor(
    private translate: TranslateService
  ) {
    this.translate.addLangs([...SUPPORTED_LOCALES]);
    // Fallback FIJO: si a un diccionario le falta una clave, ngx-translate cae
    // a es-ES en vez de renderizar el nombre crudo de la clave en pantalla.
    // Es lo que hace seguro publicar es-LA de forma incremental.
    this.translate.setDefaultLang(FALLBACK_LOCALE);
  }

  /** Elección del usuario ('es' | 'en' | 'ca'). Sobrevive al logout. */
  getChoice(): LanguageChoice {
    const saved = localStorage.getItem(CHOICE_STORAGE_KEY);
    if (LANGUAGE_CHOICES.some((l) => l.code === saved)) {
      return saved as LanguageChoice;
    }

    // Migración. Dos casos, los dos con el mismo síntoma si no se cubren: al
    // usuario le vuelve la interfaz a español aunque tuviera inglés o catalán.
    //
    //  1. Usuarios que ya estaban en la plataforma: tienen el locale viejo en
    //     `language` pero no tienen `language_choice`, porque la separación
    //     entre elección y locale es posterior.
    //  2. `language_choice` guardado como 'en-US' por una versión previa de
    //     esta rama, cuando la opción del selector era el código concreto.
    //
    // Se puede sacar cuando ya no queden clientes con el storage viejo.
    const migrated =
      choiceForLocale(saved) ?? choiceForLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
    if (migrated) {
      localStorage.setItem(CHOICE_STORAGE_KEY, migrated);
      return migrated;
    }

    return DEFAULT_CHOICE;
  }

  /**
   * Locale realmente cargado: la variante que resolvió la sesión, si hay una
   * coherente con la elección actual; si no, el default de esa familia.
   *
   * Se valida que el locale guardado pertenezca a la elección para no arrastrar
   * uno viejo tras cambiar de idioma (p. ej. quedarse en es-LA con la elección
   * ya puesta en inglés).
   */
  getLocale(): AppLocale {
    const choice = this.getChoice();
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);

    if (
      stored &&
      SUPPORTED_LOCALES.includes(stored as AppLocale) &&
      localeBelongsToChoice(stored, choice)
    ) {
      return stored as AppLocale;
    }
    return defaultLocaleForChoice(choice);
  }

  init(): Promise<void> {
    return this.useLocale(this.getLocale());
  }

  getLanguages() {
    return LANGUAGE_CHOICES;
  }

  /** El usuario eligió un idioma en el selector. */
  switchLang(choice: LanguageChoice): Promise<void> {
    localStorage.setItem(CHOICE_STORAGE_KEY, choice);
    // Se descarta la variante que hubiera resuelto la sesión anterior y se
    // arranca por el default de la familia; el backend la vuelve a resolver en
    // el próximo login.
    return this.useLocale(defaultLocaleForChoice(choice));
  }

  /**
   * Se llama al conocer la organización del usuario logueado, con el mapa
   * `language_variants` que manda el backend en el perfil.
   *
   * No se filtra por elección: el mapa cubre las tres opciones del selector.
   * Hoy `en-US` y `ca` se resuelven a sí mismas —son códigos concretos, y el
   * backend respeta la variante explícita— así que el efecto es el mismo que
   * antes. El día que el selector ofrezca `en` en vez de `en-US`, el inglés
   * empieza a resolverse por país sin tocar nada acá.
   */
  applyLanguageVariants(variants: LanguageVariants | null | undefined): Promise<void> {
    const locale = resolveLocaleFromVariants(variants, this.getChoice());
    if (locale === this.translate.currentLang) {
      return Promise.resolve();
    }
    return this.useLocale(locale);
  }

  /**
   * Logout: descarta la variante resuelta (es del usuario que se va) pero
   * conserva la elección, así el próximo login arranca en el mismo idioma.
   */
  resetResolvedLocale(): void {
    localStorage.removeItem(LOCALE_STORAGE_KEY);
    if (this.getChoice() === 'es') {
      void this.useLocale(FALLBACK_LOCALE);
    }
  }

  private useLocale(locale: AppLocale): Promise<void> {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    return firstValueFrom(this.translate.use(locale))
      .then(() => undefined)
      // Si falla la carga del diccionario dejamos bootear igual: el fallback
      // de ngx-translate cubre la UI con es-ES.
      .catch(() => undefined);
  }
}
